import React, { Fragment, useEffect, useContext, useState } from 'react';
import { Box, Grid, List, ListItem, ListItemText, Typography, styled } from '@mui/material';
import VehiclePlaceholder from 'assets/img/vehicle_placeholder.png';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';
import EditVehicleDetailsModal from './EditVehicleDetailsModal';
import { UserContext } from 'context';
import { useLocation, Link as DinLink } from 'react-router-dom';
import { buildUrl, parseInputNumber } from 'utils';
import Loading from 'components/Loading';
import { getVehicleActions } from 'utils';
import { ChangeVehicleStatusModal } from 'components/Modals';
import { useConfirm } from 'hooks';
import ItemStatus from 'components/ItemStatus';
import PropTypes from 'prop-types';

// Styled Components
const LinkContainer = styled('span')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  '& a': {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      fontWeight: 500,
    },
  },
}));

const VehicleImage = styled('img')({
  maxWidth: '100%',
});

const StyledListItem = styled(ListItem)(() => ({
  width: '100%',
  padding: '0 4px 0 0',
  borderBottom: '1px solid #EEE',
  '&:last-child': {
    border: 0,
  },
}));

const StyledListItemText = styled(ListItemText)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  textAlign: 'right',
  width: '100%',
  margin: '6px 0px',
  '& .MuiListItemText-primary': {
    fontSize: 13,
    fontWeight: 500,
    color: '#4D4F5C',
    textAlign: 'left',
    wordBreak: 'break-word',
  },
  '& .MuiListItemText-secondary': {
    fontSize: '13px',
    color: '#4D4F5C',
    fontWeight: 300,
    wordBreak: 'break-word',
    maxWidth: '70%',
  },
}));

const getUnits = (data) => {
  return data
    ? `${data?.magnitude !== undefined ? data?.magnitude : ''} ${data?.units || ''}`
    : '-';
};

const getVehicleDescription = (vehicle) =>
  `${vehicle?.vehicle_type?.year || ''} ${vehicle?.vehicle_type?.make || ''} ${
    vehicle?.vehicle_type?.model || ''
  } ${vehicle?.vehicle_type?.trim || ''}`.trim();

const getVehicleData = (vehicle = {}) => {
  const vehicleType = vehicle?.vehicle_type;

  const assetClass = vehicleType?.asset_class;

  const vehicleInfo = [
    { label: 'Name', value: vehicle.name || '-' },
    { label: 'Vehicle ID', value: vehicle?.id || '-' },
    { label: 'License Plate', value: vehicle?.license_plate || '-' },
    { label: 'VIN', value: vehicle.vin || '-' },
    { label: 'Color', value: vehicle?.color || '-' },
    { label: 'Make', value: vehicleType?.make || '-' },
    { label: 'Model', value: vehicleType?.model || '-' },
    { label: 'Model Year', value: vehicleType?.year || '-' },
    { label: i18n.t('vehicle.trim'), value: vehicleType?.trim || '-' },
    { label: 'Fuel Capacity', value: getUnits(vehicleType?.fuel_capacity) },
    { label: 'Price', value: vehicle?.price ? `$${(vehicle.price / 100).toFixed(2)}` : '-' },
    { label: 'Powertrain', value: vehicleType?.powertrain || '-' },
    // with the conditions field we validate whether or not that item should be shown
    {
      label: 'Number Of Seats',
      value: vehicle?.seats || '-',
      condition: assetClass === 'BUS',
    },
    {
      label: 'Motor',
      value: getUnits(vehicleType?.power),
      condition: assetClass === 'SCOOTER',
    },
    {
      label: 'Battery',
      value: vehicleType?.engine_type || '-',
      condition: assetClass === 'SCOOTER',
    },
    { label: 'Details', value: vehicle?.details || '-' },
    {
      label: 'Range',
      value: getUnits(vehicleType?.range),
      condition: assetClass === 'SCOOTER',
    },
  ];

  return vehicleInfo;
};

export const VehicleDetailsWidget = ({ id }) => {
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const [editionModalOpen, setEditionModalOpen] = useState(false);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);

  const vehicle = useSelector((state) => state?.vehicles?.vehicle);
  const loading = useSelector((state) => state?.vehicles?.loading);
  const error = useSelector((state) => state?.vehicles?.errors?.vehicles);
  const core = useSelector((state) => state.config?.settings?.core || {});
  const fetchingUpdateImage = useSelector((state) => state.files?.fetchingUpdateImage);
  const fetchedUpdateImageSuccess = useSelector((state) => state.files?.fetchedUpdateImageSuccess);

  const location = useLocation();
  const routeTo = buildUrl(`/vehicles/${id}`);

  const title =
    location?.pathname === routeTo ? (
      <>ID# {id}</>
    ) : (
      <DinLink to={routeTo}>
        <LinkContainer>ID# {id}</LinkContainer>
      </DinLink>
    );
  const vehicleDescription = getVehicleDescription(vehicle);

  useEffect(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id && !fetchingUpdateImage && fetchedUpdateImageSuccess) {
      dispatch(vehicleActions.fetchVehicleById(id));
    }
  }, [fetchingUpdateImage, fetchedUpdateImageSuccess]); // eslint-disable-line

  const toggleEditInformation = (e) => {
    e?.preventDefault();
    setEditionModalOpen(!editionModalOpen);
  };

  const [toggleUnlockAndEnable, UnlockAndEnableConfirm] = useConfirm({
    title: i18n.t('actions.UNLOCKANDENABLE'),
    message: i18n.t('vehicle.unlock_and_enable_confirm'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: () => dispatch(vehicleActions.setVehicleAction(id, 'UNLOCKANDENABLE')),
  });

  const handleMenuItemOnClick = (action) => {
    switch (action) {
      case 'UNLOCKANDENABLE':
        toggleUnlockAndEnable();
        break;
      case 'UNLOCK':
        dispatch(vehicleActions.setVehicleAction(id, 'UNLOCK'));
        break;
      case 'LOCK':
        dispatch(vehicleActions.setVehicleAction(id, 'LOCK'));
        break;
      default:
        dispatch(vehicleActions.setVehicleAction(id, action));
        break;
    }
  };

  const handleChangeStatus = () => {
    dispatch(vehicleActions.resetChangeStatus());
    setOpenUpdateStatus((prevState) => !prevState);
  };

  const handleOnSubmit = (newValues) => {
    let data = { ...newValues };

    delete data.asset_class;

    // remove empty fields for non-emtpy fields
    const nonEmptyField = ['price', 'seats', 'vin'];
    Object.keys(data).forEach(
      (key) => data[key] === '' && nonEmptyField.includes(key) && delete data[key]
    );

    if (data.price) {
      data.price = parseInputNumber(data.price);
    }

    dispatch(vehicleActions.updateVehicle(id, data)).then((response) => {
      if (response.ok) {
        setEditionModalOpen(false);
      }
    });
  };

  const configBrokers = core?.vehicle?.brokers || {};
  let actionsItems;
  if (configBrokers && vehicle?.brokers) {
    const actions = getVehicleActions(configBrokers, vehicle?.brokers) || [];
    actionsItems = [
      {
        title: i18n.t('vehicle.update_information'),
        onClick: toggleEditInformation,
        orgAbility: {
          subject: ['msOwnerOrg', 'plainOrg'],
        },
      },
      { title: i18n.t('vehicle.change_status'), onClick: handleChangeStatus, divider: true },
      ...(Array.isArray(actions) ? actions : []).map((item) => {
        return {
          title: i18n.t(`actions.${item}`),
          onClick: () => handleMenuItemOnClick(item),
        };
      }),
    ];
  } else {
    actionsItems = [];
  }

  return (
    <Card
      menuItems={loading ? [] : actionsItems}
      menuPermissions={{
        action: 'remote_action',
        subject: 'vehicle',
        type: 'can',
      }}
      title={i18n.t('common.vehicle')}
      headerActionType="vertical"
      data-cy="vehicle-details-widget"
      sx={{
        '& .MuiCardContent-root': {
          p: 0,
          pb: '0 !important',
        },
      }}
    >
      <>
        {loading || !id ? (
          <Box
            sx={{
              minHeight: 150,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Loading />
          </Box>
        ) : error?.type ? (
          <Box>{i18n.t(`vehicle.error.${error?.type}`, { defaultValue: error?.message })}</Box>
        ) : (
          <Box>
            <Grid container direction="row" alignItems="center" spacing={1}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h3">
                  <Box sx={{ fontSize: '1rem', fontWeight: 500 }}>{title}</Box>
                </Typography>
              </Grid>
              {vehicle?.status && (
                <Grid size={{ xs: 12 }}>
                  <ItemStatus
                    dataCy="vehicle_status"
                    variant="labelValue"
                    label={i18n.t('vehicle.status')}
                    i18nKey="status"
                    status={vehicle?.status}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Box textAlign="center">
                  <VehicleImage
                    alt={vehicle?.name || ''}
                    src={vehicle?.default_file?.thumbnail_url || VehiclePlaceholder}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h4">
                  <Box sx={{ fontSize: '1rem', fontWeight: 500 }}>{vehicleDescription}</Box>
                </Typography>
              </Grid>
            </Grid>
            <>
              <List
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'flex-start',
                }}
                data-cy="vehicle-data"
              >
                {getVehicleData(vehicle).map((val, index) => (
                  <Fragment key={index}>
                    {(val?.condition || val?.condition === undefined) && (
                      <StyledListItem>
                        <StyledListItemText primary={val.label} secondary={val.value} />
                      </StyledListItem>
                    )}
                  </Fragment>
                ))}
              </List>
            </>
          </Box>
        )}
      </>

      {openUpdateStatus && (
        <ChangeVehicleStatusModal
          open={true}
          vehicleId={parseInt(id)}
          vehicle={vehicle}
          handleClick={handleChangeStatus}
        />
      )}
      <UnlockAndEnableConfirm />
      <EditVehicleDetailsModal
        open={editionModalOpen}
        canEdit={orgType === 'msOwnerOrg' || orgType === 'plainOrg'}
        vehicle={vehicle}
        onSubmit={handleOnSubmit}
        onCancel={() => {
          setEditionModalOpen(false);
        }}
      />
    </Card>
  );
};

VehicleDetailsWidget.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
